import axios from 'axios';
import { getAuthUrl } from './auth';
import { performLogout } from '../utils/authUtils';
import { keyPairReady, getPublicKeyBase64 } from '../crypto/e2e';

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/nodeapi',
});

// Flag per evitare redirect multipli
let isRedirecting = false;

// Funzione per gestire il logout
const handleLogout = (errorResponse = null, apiUrl = null, requestData = null) => {
  // Traccia l'errore API solo se ci sono dettagli disponibili
  if (errorResponse) {
    try {
      // Recupera il token (potrebbe essere scaduto)
      const token = localStorage.getItem('token') || '';
      
      // Prepara i dati per il log indipendentemente dallo stato del token
      const errorStatus = errorResponse.status || 'unknown';
      const errorMessage = errorResponse.statusText || 'Unknown error';
      
      // Recupera info utente per il log
      let userInfo = {};
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          userInfo = JSON.parse(userStr);
        }
      } catch (e) {
        console.error('Error parsing user data', e);
      }
      
      // Prepara il payload per il log
      const payload = {
        level: 'error',
        serviceName: 'cediFE',
        message: {
          action: 'api_error',
          description: `API Error: ${errorStatus} ${errorMessage}`,
          timestamp: new Date().toISOString(),
          pagePath: window.location.pathname,
          sessionId: localStorage.getItem('session_id') || 'unknown',
          userInfo,
          metadata: {
            logType: 'error',
            errorStatus,
            errorMessage,
            apiUrl,
            requestData: typeof requestData === 'object' ? 
                       JSON.stringify(requestData).substring(0, 1000) : // Limita la lunghezza
                       requestData,
            userAgent: navigator.userAgent
          }
        }
      };
      
      // Usa sendBeacon per l'invio più affidabile (funziona anche quando la pagina si chiude)
      // e passa il token come query parameter invece che nell'header
      const payloadBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      const beaconSent = navigator.sendBeacon(`/nodeapi/logs?token=${encodeURIComponent(token)}`, payloadBlob);
      
      // Fallback a fetch se sendBeacon non funziona
      if (!beaconSent) {
        fetch('/nodeapi/logs', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
            // Non includiamo il token nell'header per evitare problemi con token scaduti
            // Il server dovrà accettare questa richiesta anche senza autenticazione
          },
          body: JSON.stringify(payload),
          keepalive: true,
          priority: 'high'
        }).catch(err => {
          console.error('Failed to log API error using fetch fallback:', err);
        });
      }
    } catch (e) {
      console.error('Error logging API error:', e);
    }
  }
  
  // Call the shared logout function
  // We don't want to redirect immediately if we're handling a 401 during a fetch
  // to avoid potential redirect loops
  performLogout(false);
  
  // Se non stiamo già facendo un redirect, procedi
  if (!isRedirecting) {
    isRedirecting = true;
    
    // Salva l'URL corrente per il redirect post-login
    const currentPath = window.location.pathname;
    const authUrl = getAuthUrl(currentPath);

    // Effettua il redirect
    window.location.href = authUrl;

    // Reset del flag dopo un breve delay
    setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  }
};

// Inject session public key so backend can encrypt responses for this client
axiosInstance.interceptors.request.use(async (config) => {
  await keyPairReady;
  config.headers['X-Public-Key'] = getPublicKeyBase64();
  return config;
});

// Interceptor per gestire gli errori di autenticazione
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Gestisci gli errori 401 (Unauthorized)
    if (error.response?.status === 401) {
      // Passa i dettagli dell'errore al metodo handleLogout
      handleLogout(
        error.response,
        error.config?.url, 
        error.config?.data
      );
    }
    return Promise.reject(error);
  }
);

export { handleLogout };
export default axiosInstance;
